import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import GranjaArticuloCodigoField from '../../components/granja/GranjaArticuloCodigoField';
import GranjaMovimientosLinkButton from '../../components/granja/GranjaMovimientosLinkButton';
import LoteSelectorButtons from '../../components/granja/LoteSelectorButtons';
import NumericKeypad from '../../components/granja/NumericKeypad';
import { useFechaOperativa } from '../../contexts/FechaOperativaContext';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import ajusteInventarioService from '../../services/ajusteInventarioService';
import granjaService, { GranjaConfig, resolveNroSucOperativo } from '../../services/granjaService';
import { getApiErrorMessage } from '../../utils/apiError';
import { toLocalDateStringYMD } from '../../utils/formatters';
import { saveGranjaConfig } from '../../utils/granjaConfigPersist';

const GranjaAves: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { preferidaNro } = useSucursalesUsuarioContext();
  const { fechaYmd } = useFechaOperativa();
  const diaOperativo = fechaYmd ?? toLocalDateStringYMD();
  const [tab, setTab] = useState(0);
  const [config, setConfig] = useState<GranjaConfig | null>(null);
  const [loteCodigo, setLoteCodigo] = useState<string | null>(null);
  const [cantidadStr, setCantidadStr] = useState('');
  const [cabezasVivas, setCabezasVivas] = useState(0);
  const [saving, setSaving] = useState(false);

  const [altaCodigo, setAltaCodigo] = useState('');
  const [altaAves, setAltaAves] = useState('');
  const [altaBruto, setAltaBruto] = useState('');
  const [altaCabezas, setAltaCabezas] = useState('');
  const [altaTolerancia, setAltaTolerancia] = useState('0.5');
  const [altaFecha, setAltaFecha] = useState(diaOperativo);

  const reloadCabezas = async (codigo: string | null) => {
    if (!codigo) {
      setCabezasVivas(0);
      return;
    }
    try {
      const kpis = await granjaService.getKpis(diaOperativo, diaOperativo);
      const loteKpi = kpis.lotes.find((l) => l.codigoLote === codigo);
      setCabezasVivas(Number(loteKpi?.cabezasVivas ?? 0));
    } catch {
      setCabezasVivas(0);
    }
  };

  const reloadConfig = async () => {
    const cfg = await granjaService.getConfig();
    setConfig(cfg);
    const nextCodigo =
      loteCodigo ?? cfg.lotes.find((l) => l.activo)?.codigo ?? null;
    if (!loteCodigo && nextCodigo) setLoteCodigo(nextCodigo);
    await reloadCabezas(nextCodigo);
    return cfg;
  };

  useEffect(() => {
    (async () => {
      try {
        await reloadConfig();
      } catch {
        enqueueSnackbar('Error al cargar configuración', { variant: 'error' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lote = useMemo(
    () => config?.lotes.find((l) => l.codigo === loteCodigo) ?? null,
    [config, loteCodigo],
  );

  useEffect(() => {
    void reloadCabezas(loteCodigo);
  }, [loteCodigo, diaOperativo]);

  const cantidad = Number(cantidadStr) || 0;

  const registrarBaja = async (tipo: 'MORTANDAD' | 'DESCARTE') => {
    if (!config?.depositoGranja || !lote) {
      enqueueSnackbar('Seleccioná lote y configurá depósito', { variant: 'warning' });
      return;
    }
    if (cantidad <= 0) {
      enqueueSnackbar('Ingresá una cantidad', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      const nroSuc = await resolveNroSucOperativo(preferidaNro);
      if (!nroSuc) {
        enqueueSnackbar('No hay sucursal operativa asignada al usuario', { variant: 'warning' });
        return;
      }
      await granjaService.registrarBaja({
        codigoLote: lote.codigo,
        cantidad,
        nroSuc,
        tipo,
        fecha: diaOperativo,
        observaciones: `${tipo === 'MORTANDAD' ? 'Mortandad' : 'Descarte'} lote ${lote.codigo}`,
      });
      enqueueSnackbar('Registro guardado', { variant: 'success' });
      setCantidadStr('');
      await reloadCabezas(lote.codigo);
    } catch (err: unknown) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al registrar'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAltaLote = async () => {
    if (!config?.depositoGranja) {
      enqueueSnackbar('Configurá el depósito Granja', { variant: 'warning' });
      return;
    }
    if (!altaCodigo.trim() || !altaAves.trim() || !altaBruto.trim()) {
      enqueueSnackbar('Completá código y artículos del lote', { variant: 'warning' });
      return;
    }
    const cabezas = Number(altaCabezas) || 0;
    if (cabezas <= 0) {
      enqueueSnackbar('Indicá cabezas iniciales', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      const nextConfig: GranjaConfig = {
        ...config,
        lotes: [
          ...(config.lotes ?? []),
          {
            codigo: altaCodigo.trim(),
            articuloAves: altaAves.trim(),
            articuloHuevoBruto: altaBruto.trim(),
            fechaIngreso: altaFecha,
            cabezasIniciales: cabezas,
            toleranciaPct: Number(altaTolerancia) || 0,
            activo: true,
          },
        ],
      };
      await saveGranjaConfig(nextConfig);
      const nroSuc = await resolveNroSucOperativo(preferidaNro);
      if (!nroSuc) {
        enqueueSnackbar('No hay sucursal operativa asignada al usuario', { variant: 'warning' });
        return;
      }
      await ajusteInventarioService.createAjuste(
        {
          fechaEmision: diaOperativo,
          nroSuc,
          descripcion: `Alta lote ${altaCodigo.trim()}`,
          motivoCodigo: 'ALTA_LOTE',
          nroUbicacionOrigen: 0,
          nroUbicacionDestino: config.depositoGranja,
        } as never,
        [
          {
            codigoArticulo: altaAves.trim(),
            descripcion: `Aves ${altaCodigo.trim()}`,
            cantidad: cabezas,
            costo: 0,
            notas: 'Alta de lote',
          },
        ] as never,
        'positivo',
      );
      enqueueSnackbar('Lote dado de alta', { variant: 'success' });
      setLoteCodigo(altaCodigo.trim());
      await reloadConfig();
      setAltaCodigo('');
      setAltaAves('');
      setAltaBruto('');
      setAltaCabezas('');
    } catch (err: unknown) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al dar de alta el lote'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Control de aves
              </Typography>
              <GranjaMovimientosLinkButton
                tipo={tab === 0 ? 'MORTANDAD' : tab === 1 ? 'ALTA_LOTE' : 'DESCARTE'}
              />
            </Stack>

            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
            >
              <Tab label="Mortandad" />
              <Tab label="Alta lote" />
              <Tab label="Descarte" />
            </Tabs>

            {tab !== 1 ? (
              <>
                <LoteSelectorButtons
                  lotes={config?.lotes ?? []}
                  selected={loteCodigo}
                  onSelect={setLoteCodigo}
                />
                <Typography variant="body1">
                  Cabezas vivas: <strong>{cabezasVivas}</strong>
                  {lote ? ` · Tolerancia ${lote.toleranciaPct}%` : null}
                </Typography>
                <NumericKeypad
                  label="Cantidad de aves"
                  value={cantidadStr}
                  onChange={setCantidadStr}
                  maxDecimals={0}
                />
                {tab === 0 ? (
                  <Button
                    variant="contained"
                    color="error"
                    size="large"
                    disabled={saving}
                    onClick={() => registrarBaja('MORTANDAD')}
                    sx={{ minHeight: 56, fontWeight: 700 }}
                  >
                    Registrar mortandad
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="warning"
                    size="large"
                    disabled={saving}
                    onClick={() => registrarBaja('DESCARTE')}
                    sx={{ minHeight: 56, fontWeight: 700 }}
                  >
                    Registrar descarte
                  </Button>
                )}
              </>
            ) : (
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary">
                  El artículo de aves debe existir previamente en Inventario. Esta pantalla agrega
                  el lote a la config y el stock inicial.
                </Typography>
                <TextField
                  label="Código lote"
                  value={altaCodigo}
                  onChange={(e) => setAltaCodigo(e.target.value)}
                  fullWidth
                />
                <GranjaArticuloCodigoField
                  label="Artículo aves"
                  value={altaAves}
                  dialogTitle="Artículo aves"
                  size="medium"
                  onChange={setAltaAves}
                />
                <GranjaArticuloCodigoField
                  label="Artículo huevo bruto"
                  value={altaBruto}
                  dialogTitle="Artículo huevo bruto"
                  size="medium"
                  onChange={setAltaBruto}
                />
                <TextField
                  label="Fecha ingreso"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={altaFecha}
                  onChange={(e) => setAltaFecha(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Cabezas iniciales"
                  type="number"
                  value={altaCabezas}
                  onChange={(e) => setAltaCabezas(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Tolerancia % diaria"
                  type="number"
                  value={altaTolerancia}
                  onChange={(e) => setAltaTolerancia(e.target.value)}
                  fullWidth
                />
                <Button
                  variant="contained"
                  size="large"
                  disabled={saving}
                  onClick={handleAltaLote}
                  sx={{ minHeight: 56, fontWeight: 700 }}
                >
                  Dar de alta lote
                </Button>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GranjaAves;
